<template>
  <div class="surface-100 w-full">
    <div v-if="doc.display_model_data">
      <WebComponent
        :key="doc.link || doc.web_page_id"
        :item="mapProps(doc, layout?.mappings ?? {})"
        :programme="programme"
      />
    </div>
    <div v-else class="flex flex-column gap-4 align-items-center w-full">
      <div
        v-for="section in doc.web_sections.filter((section) => section?.web_contents?.length)"
        :key="section.id ?? section.name"
        :class="[
          'flex justify-content-center w-full',
          {
            'bg-cover bg-center bg-no-repeat': section?.img,
          },
        ]"
        :style="getImgPath(section?.img)"
      >
        <div :class="`${getClass(section)} w-full`">
          <component
            v-if="section.web_component"
            :is="components[section.web_component]"
            :items="section.web_contents"
            :group_by="section.group_by"
          />

          <WebComponent
            v-else
            v-for="content in section.web_contents"
            :key="`${content.web_section_id}-${content.row_order}`"
            :item="content"
            :programme="programme"
            :grid-gutter="getGridGutter(section)"
            auto-height
          />
        </div>
      </div>
    </div>
    <SubscribeForm v-if="brevoListId" :list-id="brevoListId" />
  </div>
</template>

<script setup>
import { mapProps, components } from '~/composables/useUtils'
const props = defineProps({
  doc: {
    type: Object,
    required: true,
  },
})
const programme = computed(() => (props.doc.package === 'programme' ? props.doc : undefined))
const brevoListId = computed(() => {
  if (props.doc.package !== 'web_content' || !props.doc.display_model_data) {
    return null
  }

  const listId = Number(props.doc.brevo_list_id)
  return Number.isSafeInteger(listId) && listId > 0 ? listId : null
})

// Adjacent grid items combine their padding, so each wrapper uses roughly half the requested gap.
const GRID_GUTTER_BY_GAP = {
  0: 0,
  1: 1,
  2: 1,
  3: 2,
  4: 2,
  5: 3,
  6: 4,
  7: 5,
  8: 5,
}

const getCachedLayout = (key, nuxtApp) => {
  return nuxtApp.payload.data[key] ?? nuxtApp.static.data[key]
}

const layoutKey = computed(() => `${props.doc.parent_id}-layout`)
const { data: layout } = await useAsyncData(
  layoutKey,
  () => {
    if (!props.doc.display_model_data) {
      return null
    }

    return queryContent()
      .where({
        web_page_id: props.doc.parent_id,
      })
      .only(['layout'])
      .findOne()
  },
  {
    dedupe: 'defer',
    getCachedData: getCachedLayout,
    watch: [() => props.doc.display_model_data],
  }
)

const getClass = (section) => {
  const layout = section.layout ?? ''
  const height = section.height ? `h-${section.height}` : ''
  const direction = section.direction ? `flex-${section.direction}` : ''
  const wrap = section.wrap ? `flex-${section.wrap}` : ''
  const placement = section.placement ? `justify-content-${section.placement}` : ''
  const align = section.align ? `align-items-${section.align}` : ''
  const gap = section.gap && !usesGridGutters(section) ? `gap-${section.gap}` : ''
  const padding = section.padding ? `p-${section.padding}` : ''
  let bgColor = section.bg_color ? `bg-${section.bg_color}` : ''
  if (section.bg_opacity) {
    bgColor += `-${section.bg_opacity}`
  }
  return [layout, direction, wrap, placement, align, gap, padding, bgColor, height]
    .filter(Boolean)
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim()
}

const usesGridGutters = (section) => {
  const hasGap = section.gap !== undefined && section.gap !== null && section.gap !== ''

  return section.layout === 'grid' && !section.web_component && hasGap
}

const getGridGutter = (section) => {
  return usesGridGutters(section) ? GRID_GUTTER_BY_GAP[section.gap] : undefined
}

const getImgPath = (uuid) => {
  return uuid ? `background-image: url('/media/${uuid}.webp')` : ''
}
</script>

<style lang="scss">
.post_title {
  font-size: 28px;
}
.post_content {
  font-size: 22px;
}

a:link,
a:visited {
  text-decoration: none;
}

a:link:hover,
a:visited:active {
  text-decoration: underline;
}

i {
  color: $sats-green;
}
</style>
